import moment from "moment";
import { isNumeric } from "common/helpers/utils";
import {
  HOUR_RANGE,
  MONTH_RANGE,
  YEAR_RANGE,
} from "../../Managers/People/utils/enums";
// these validations return null (no error) or a string representing the error message

export const RATE_RANGES = {
  hourly: HOUR_RANGE,
  monthly: MONTH_RANGE,
  yearly: YEAR_RANGE,
};

export const validDate = (d, min = null, max = null) => {
  if (!d) {
    return null;
  }

  const date = moment(d);
  if (!date._isValid) {
    return "Please enter a valid date";
  }

  if (min && date.isBefore(min)) {
    return `Date must be on or after ${min.format("M/D/YYYY")}`;
  }

  if (max && date.isAfter(max)) {
    return `Date must be on or before ${max.format("M/D/YYYY")}`;
  }
};

export const validateRate = (min, max) => Number(min) < Number(max);

export const validateMinRate = (minRate, compensationType) => {
  const rateRange = RATE_RANGES[compensationType];

  return Number(minRate) >= rateRange.MIN && Number(minRate) <= rateRange.MAX;
};

export const validateMaxRate = (maxRate, compensationType) => {
  const rateRange = RATE_RANGES[compensationType];

  return Number(maxRate) >= rateRange.MIN && Number(maxRate) <= rateRange.MAX;
};

export const validateMinAndMaxRates = (
  isEORClient,
  minRate,
  maxRate,
  compensationType
) => {
  if (isEORClient) return true;

  return (
    validateMinRate(minRate, compensationType) &&
    validateMaxRate(maxRate, compensationType)
  );
};

export const validBirthDate = (d) => {
  if (
    validDate(
      d,
      moment().subtract(120, "years"),
      moment().subtract(10, "years")
    )
  ) {
    return "Please check the birth year entered";
  }
};

export const validZip = (z) => {
  if (!z) {
    return null;
  }

  if (!z.match(/^\d{5}$/) && !z.match(/^[a-zA-Z0-9]{6}$/)) {
    return "Please enter valid zip/postal code";
  }

  return null;
};

export const validICAddress = (address) => {
  if (!(address || "").trim()) {
    return "Please enter valid address";
  }

  return null;
};

export const validICZip = (zip) => {
  if (!(zip || "").trim()) {
    return "Please enter valid zip/postal code";
  }

  return null;
};

export const validSsn = (ssn) => {
  if (!ssn) {
    return null;
  }

  const allDigitsPattern = /^\d{3}-\d{2}-\d{4}$/;
  const maskedPattern = /^\*{3}-\*{2}-\d{4}$/;

  if (!allDigitsPattern.test(ssn) && !maskedPattern.test(ssn)) {
    return "Please enter a valid social security number";
  }

  return null;
};

export const validEIN = (ein) => {
  if (!ein) {
    return null;
  }

  const allDigitsPattern = /^\d{2}-\d{7}$/;
  const maskedPattern = /^\*{2}-\*{3}\d{4}$/;

  if (!allDigitsPattern.test(ein) && !maskedPattern.test(ein)) {
    return "Please enter a valid employer identification number";
  }

  return null;
};

export const validSIN = (sin) => {
  if (!sin) {
    return null;
  }

  const allDigitsPattern = /^\d{3}-\d{3}-\d{3}$/;
  const maskedPattern = /^\*{3}-\*{2}\d{1}-\d{3}$/;

  if (!allDigitsPattern.test(sin) && !maskedPattern.test(sin)) {
    return "Please enter a valid social insurance number";
  }

  return null;
};

export const validPhone = (number) => {
  if (!number) return null;

  // These are the minimum and maximum lengths for a possible international phone number
  const MINIMUM_LENGTH = 7;
  const MAXIMUM_LENGTH = 15;

  const isValid =
    number.length >= MINIMUM_LENGTH && number.length <= MAXIMUM_LENGTH;
  const error = isValid ? null : "Please enter a valid phone number";

  return error;
};

export const validRoutingNumber = (routingNumber) => {
  if (!routingNumber) {
    return "Routing number must be present";
  }

  // Strip whitespace and remove hyphens
  const cleanedRoutingNumber = routingNumber.replace(/[\s-]/g, "");

  // Check for masked pattern (e.g., *****1111)
  const maskedPattern = /^\*+\d+$/;
  if (maskedPattern.test(cleanedRoutingNumber)) {
    return null; // Accept masked values
  }

  if (cleanedRoutingNumber.length > 9) {
    return "Routing number is too long, Please make sure you're not entering your account number here";
  }

  // Pad with leading zeros if needed
  let paddedRoutingNumber = cleanedRoutingNumber;
  while (paddedRoutingNumber.length < 9) {
    paddedRoutingNumber = `0${paddedRoutingNumber}`;
  }

  // ADP validation using mask 371371371
  const mask = "371371371";
  const sumProduct = paddedRoutingNumber
    .split("")
    .map((digit, index) => parseInt(digit, 10) * parseInt(mask[index], 10))
    .reduce((sum, val) => sum + val, 0);

  if (sumProduct <= 0 || sumProduct % 10 !== 0) {
    return "Invalid routing number. Please make sure you're not entering your account number here";
  }

  return null;
};

export const validCADRoutingNumber = (routingNumber) => {
  if (!routingNumber) {
    return "Routing number must be present";
  }

  // Check for masked pattern (e.g., *****111)
  const maskedPattern = /^\*+\d+$/;
  if (maskedPattern.test(routingNumber)) {
    return null; // Accept masked values
  }

  if (routingNumber.length !== 8) {
    return "Routing number should be eight digits";
  }

  return null;
};

export const validAccountNumber = (accountNumber) => {
  if (!+accountNumber) {
    return "Account number must be present";
  }

  if (!isNumeric(+accountNumber)) {
    return "Account number must be a number";
  }

  return null;
};

export const validADPStreet = (street) => {
  if (!street) {
    return null;
  }

  if (street.length > 30) {
    return "Street should be less than 30 characters";
  }

  if (street.match(/\s\s/)) {
    return "Please make sure there aren't two spaces in a row in the address.";
  }

  return null;
};

// Anchored at both ends; the trailing dot stays optional because 74 live accounts store one.
const EMAIL_REGEXP =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}\.?$/;

export const validEmail = (email) => EMAIL_REGEXP.test(email);

export const validRequireResume = (resumeFpurl) => {
  if (resumeFpurl) {
    return null;
  }

  return "Please upload your resume";
};
